import { unifiedApi } from "@/common";
import { ref, computed } from "vue";
import { IPayPalApprovalResponse } from "@/common/api/unifiedPortal/interfaces";
import { defineStore } from "pinia";
import { pageStates, purchaseErrorsNotifications } from "@/components/Buy/BuyEnums";
import { logEvent } from "@/common/logger";
import { payPalCheckOutPage } from "@/globalEnums";
import { useBuyFlowStore } from "./buyFlow";

export const usePayPalStore = defineStore("payPal", () => {
  const payPalBillingAgreementResponse = ref<IPayPalApprovalResponse | null>();
  const payPalCheckoutFrom = ref<payPalCheckOutPage>(payPalCheckOutPage.BuyFlowPayment);
  const purchaseError = ref<purchaseErrorsNotifications | null>(null);
  const isEnhancedCart = ref<boolean>(false);
  const isReturning = ref<boolean>(false);
  const isCancelling = ref<boolean>(false);

  //store the shipping address to be used in Review order Page
  const payPalShippingAddress = computed(() => {
    return payPalBillingAgreementResponse.value?.shippingAddress;
  });

  //store the shipping address to be used in Review order Page
  const payPalPaymentMethodId = computed(() => {
    return payPalBillingAgreementResponse.value?.paymentMethodId;
  });

  const payPalEmail = computed(() => {
    return payPalBillingAgreementResponse.value?.email;
  });

  //Generate route to redirect to when paypal return success
  const getPayPalSuccessRoute = computed(() => {
    let path = "/";
    let queryParams = {};
    if (payPalBillingAgreementResponse.value) {
      if (isEnhancedCart.value) {
        path = "buy/cart";
        queryParams = "";
      } else {
        path = "buy";
        queryParams = Object.assign(
          {},
          {
            step: pageStates.reviewOrder,
            from: "Paypal",
          }
        );
      }
    }
    return { path: path, query: queryParams };
  });
  //Generate route to redirect to when paypal return cancel
  const getPayPalCancelRoute = computed(() => {
    let path = "/";
    let queryParams = {};
    if (isEnhancedCart.value) {
      path = "buy/cart";
      queryParams = "";
    } else {
      path = "buy";
      queryParams = Object.assign(
        {},
        {
          step: pageStates.payment,
          from: "Paypal",
        }
      );
    }
    return { path: path, query: queryParams };
  });
  async function setPayPalBillingAgreementResponse(ba_token: string) {
    const buyflowStore = useBuyFlowStore();
    const payPalCheckOutFrom = sessionStorage["PayPal_CheckOutPage"] ?? payPalCheckOutPage.BuyFlowPayment;
    let brand: string | undefined;
    if (payPalCheckOutFrom === payPalCheckOutPage.BuyFlowPayment) {
      sessionStorage.removeItem("Current_Subscription_Brand"); //remove un necessary session
      buyflowStore.cart = JSON.parse(sessionStorage["BuyFlow_Paypal_cart"]);
      brand = buyflowStore.cart?.brand;
    } else {
      brand = sessionStorage["Current_Subscription_Brand"];
    }
    //The response we get back contains the address of the user,their email and the paymentMethodId
    //send these back to the buy flow with that information
    payPalBillingAgreementResponse.value = (await unifiedApi.payPalApprove(ba_token, brand ?? "WEBROOT")).data;
    logEvent("PayPalBillingAgreementResponse", "paypal", payPalBillingAgreementResponse.value);
  }

  //check and set if cart has AllState with Non US Paypal Shipping Address
  //if yes should display available in the U.S and complete checkout must be disabled
  const payPalShippingAddressIsNonUS = computed(() => {
    if (payPalBillingAgreementResponse.value?.shippingAddress) {
      if (payPalBillingAgreementResponse.value?.shippingAddress.countryCode?.toLowerCase() != "us") {
        return true;
      }
    }
    return false;
  });

  function SetPayPalCheckoutFrom(page: payPalCheckOutPage) {
    payPalCheckoutFrom.value = page;
  }

  function clearPayPalBillingAgreementResponse() {
    payPalBillingAgreementResponse.value = null;
  }

  //Adding this because we are having issues displaying PayPal info when selecting an already existing PayPal
  //payment method from the drop down in the buy flow
  function setPayPalBillingAgreementResponseManually(response: IPayPalApprovalResponse) {
    payPalBillingAgreementResponse.value = response;
  }

  return {
    setPayPalBillingAgreementResponse,
    getPayPalSuccessRoute,
    getPayPalCancelRoute,
    payPalShippingAddress,
    payPalPaymentMethodId,
    payPalEmail,
    payPalShippingAddressIsNonUS,
    SetPayPalCheckoutFrom,
    payPalCheckoutFrom,
    clearPayPalBillingAgreementResponse,
    purchaseError,
    setPayPalBillingAgreementResponseManually,
    isEnhancedCart,
    isReturning,
    isCancelling,
  };
});
